<template>
	<div class="wrapper">
		<b-row>
			<b-col cols="12">
				<b-card header-tag="header">
					<template v-slot:header>
						<i class="icon-clock"></i> Location tracking
					</template>

					<b-card-body>
						<b-form>
							<!-- Office selector -->
							<b-form-group label="Office" label-for="office" :label-cols="3" :horizontal="true">
								<multiselect
									id="office"
									v-model="selectedOffice"
									:options="officeList"
									:close-on-select="false"
									:searchable="true"
									label="name"
									placeholder="Pick a value"
									@input="loadSettingsForOffice()"
								/>
							</b-form-group>

							<!-- Stop mode -->
							<b-form-group
								v-if="modeRow"
								label="Stop tracking at"
								:label-cols="3"
								:horizontal="true"
							>
								<b-form-select
									v-model="modeChoice"
									:options="modeOptions"
									class="mb-3"
									value-field="value"
									text-field="name"
								/>
							</b-form-group>

							<!-- Custom time HH:mm (only when mode = CUSTOM) -->
							<b-form-group
								v-if="modeRow && modeChoice === MODE_CUSTOM"
								label="Custom time (HH:mm)"
								:label-cols="3"
								:horizontal="true"
							>
								<b-form-input type="time" v-model="customTimeHHmm" step="60" />
							</b-form-group>

							<!-- Location Force Stop Enable/Disable -->
							<b-form-group
								v-if="forceStopRow"
								label="Location Force Stop"
								:label-cols="3"
								:horizontal="true"
							>
								<b-form-select
									v-model="forceStopChoice"
									:options="forceStopOptions"
									class="mb-3"
									value-field="value"
									text-field="name"
								/>
							</b-form-group>

							<!-- Location Separation Enable/Disable -->
							<b-form-group
								v-if="locationSeparationRow"
								label="Location Separation"
								:label-cols="3"
								:horizontal="true"
							>
								<b-form-select
									v-model="locationSeparationChoice"
									:options="locationSeparationOptions"
									class="mb-3"
									value-field="value"
									text-field="name"
								/>
							</b-form-group>

							<!-- Save -->
							<b-button
								v-if="$store.getters.canWrite"
								type="button"
								variant="success"
								:disabled="!selectedOffice || !modeRow || !timeRow || !forceStopRow || !locationSeparationRow"
								@click="save"
							>
								Save
							</b-button>
						</b-form>
					</b-card-body>
				</b-card>
			</b-col>
		</b-row>
	</div>
</template>

<script>
import toastService from "@/services/toastService";
import officeService from "@/services/officeService";
import generalSettingsService from "@/services/generalSettingsService";
import generalSettingsGroupIds from "@/services/generalSettingsGroupIds";
import Multiselect from "vue-multiselect";

// UI values
const MODE_SHIFT_END = "SHIFT_END";
const MODE_CUSTOM    = "CUSTOM";

const KEY_MODE_CODE  = "LOCATION_TRACKING_STOP_SHIFT_END";   // id 40
const KEY_TIME_CODE  = "LOCATION_TRACKING_STOP_CUSTOM_TIME"; // id 41
const KEY_FORCE_STOP_CODE = "LOCATION_FORCE_STOP";           // id 42
const KEY_LOCATION_SEPARATION_CODE = "LOCATION_SEPARATION";  // id 43 (or whatever ID you use)


export default {
	name: "LocationTrackingSettings",
	components: { Multiselect },
	data() {
		return {
			officeList: [],
			selectedOffice: null,

			modeRow: null,        // the MODE row (keyCode = KEY_MODE_CODE)
			timeRow: null,        // the TIME row (keyCode = KEY_TIME_CODE)
			forceStopRow: null,   // the FORCE STOP row (keyCode = KEY_FORCE_STOP_CODE)
			locationSeparationRow: null, // the LOCATION SEPARATION row

			modeOptions: [],
			modeChoice: MODE_SHIFT_END,
			customTimeHHmm: "00:00",

			forceStopOptions: [],
			forceStopChoice: "false",

			locationSeparationOptions: [],
			locationSeparationChoice: "false",

			MODE_CUSTOM
		};
	},
	methods: {
		effectiveValue(gk) {
			if (!gk) return "0";
			if (gk.setValue !== undefined && gk.setValue !== null && gk.setValue !== "") return gk.setValue;
			if (gk.defaultValue !== undefined && gk.defaultValue !== null && gk.defaultValue !== "") return gk.defaultValue;
			return "0";
		},
		isNumeric(val) {
			if (val === null || val === undefined) return false;
			return /^\d+$/.test(String(val).trim());
		},

		loadSettingsForOffice() {
			this.modeRow = null;
			this.timeRow = null;
			this.forceStopRow = null;
			this.locationSeparationRow = null;
			this.modeOptions = [];
			this.modeChoice = MODE_SHIFT_END;
			this.customTimeHHmm = "00:00";
			this.forceStopChoice = "false";
			this.locationSeparationChoice = "false";
			if (!this.selectedOffice) return;

			generalSettingsService.getGeneralSettings(
				{ groupId: generalSettingsGroupIds.LOCATION_TRACKING_SETTINGS_GROUP_ID, officeId: this.selectedOffice.id },
				(resp) => {
					const rows = Array.isArray(resp) ? resp : (resp && resp.generalSettingsGroupKeys) || [];
					console.log("Loaded location tracking settings:", rows);
					const mode = rows.find(r => r && r.generalSettingsKey && r.generalSettingsKey.keyCode === KEY_MODE_CODE);
					const time = rows.find(r => r && r.generalSettingsKey && r.generalSettingsKey.keyCode === KEY_TIME_CODE);
					const forceStop = rows.find(r => r && r.generalSettingsKey && r.generalSettingsKey.keyCode === KEY_FORCE_STOP_CODE);
					const locationSeparation = rows.find(r => r && r.generalSettingsKey && r.generalSettingsKey.keyCode === KEY_LOCATION_SEPARATION_CODE);

					if (!mode || !time || !forceStop || !locationSeparation) {
						toastService.showErrorToast("Location tracking keys not found for this office/group");
						return;
					}

					// parse mode options
					let pv = mode.generalSettingsKey.possibleValues;
					if (pv && pv !== "null") {
						try { pv = (typeof pv === "string") ? JSON.parse(pv) : pv; } catch (e) { pv = null; }
					} else { pv = null; }
					this.modeOptions =
						(pv && Array.isArray(pv.values) && pv.values.length)
							? pv.values
							: [
								{ name: "Shift end time", value: MODE_SHIFT_END },
								{ name: "Custom time",    value: MODE_CUSTOM }
							];

					// parse force stop options
					let fsPv = forceStop.generalSettingsKey.possibleValues;
					if (fsPv && fsPv !== "null") {
						try { fsPv = (typeof fsPv === "string") ? JSON.parse(fsPv) : fsPv; } catch (e) { fsPv = null; }
					} else { fsPv = null; }
					this.forceStopOptions =
						(fsPv && Array.isArray(fsPv.values) && fsPv.values.length)
							? fsPv.values
							: [
								{ name: "Enable", value: "true" },
								{ name: "Disable", value: "false" }
							];

					// parse location separation options
					let lsPv = locationSeparation.generalSettingsKey.possibleValues;
					if (lsPv && lsPv !== "null") {
						try { lsPv = (typeof lsPv === "string") ? JSON.parse(lsPv) : lsPv; } catch (e) { lsPv = null; }
					} else { lsPv = null; }
					this.locationSeparationOptions =
						(lsPv && Array.isArray(lsPv.values) && lsPv.values.length)
							? lsPv.values
							: [
								{ name: "Enable", value: "true" },
								{ name: "Disable", value: "false" }
							];

					this.modeRow = mode;           // id 40
					this.timeRow = time;           // id 41
					this.forceStopRow = forceStop; // id 42
					this.locationSeparationRow = locationSeparation; // id 43

					// Decide UI based on the time key: numeric => CUSTOM; else SHIFT_END
					const timeRaw = this.effectiveValue(time.generalSettingsKey);
					const isNumeric = /^\d+$/.test(String(timeRaw || "").trim());
					if (isNumeric && parseInt(timeRaw, 10) > 0) {
						this.modeChoice = MODE_CUSTOM;
						this.customTimeHHmm = this.msToHHmm(parseInt(timeRaw, 10));
					} else {
						this.modeChoice = MODE_SHIFT_END;
						this.customTimeHHmm = "00:00";
					}

					// Set force stop value
					const forceStopRaw = this.effectiveValue(forceStop.generalSettingsKey);
					this.forceStopChoice = forceStopRaw === "true" ? "true" : "false";

					// Set location separation value
					const locationSeparationRaw = this.effectiveValue(locationSeparation.generalSettingsKey);
					this.locationSeparationChoice = locationSeparationRaw === "true" ? "true" : "false";
				},
				(msg) => toastService.showErrorToast(msg || "Failed to load settings")
			);
		},

		save() {
			if (!this.selectedOffice || !this.modeRow || !this.timeRow || !this.forceStopRow || !this.locationSeparationRow) {
				return toastService.showErrorToast("Please select an office");
			}

			const settings = [];

			// 1. Location Force Stop setting
			settings.push({
				groupId: this.forceStopRow.groupId,
				keyId: this.forceStopRow.generalSettingsKey.id,          // 42
				keyCode: this.forceStopRow.generalSettingsKey.keyCode,   // "LOCATION_FORCE_STOP"
				setValue: this.forceStopChoice,
				officeGroupKeyId: this.forceStopRow.officeGroupKeyId
			});

			// 2. Location Separation setting
			settings.push({
				groupId: this.locationSeparationRow.groupId,
				keyId: this.locationSeparationRow.generalSettingsKey.id,          // 43
				keyCode: this.locationSeparationRow.generalSettingsKey.keyCode,   // "LOCATION_SEPARATION"
				setValue: this.locationSeparationChoice,
				officeGroupKeyId: this.locationSeparationRow.officeGroupKeyId
			});

			// 3. Mode/time setting based on selection
			if (this.modeChoice === MODE_SHIFT_END) {
				settings.push({
					groupId: this.modeRow.groupId,
					keyId: this.modeRow.generalSettingsKey.id,          // 40
					keyCode: this.modeRow.generalSettingsKey.keyCode,   // "LOCATION_TRACKING_STOP_SHIFT_END"
					setValue: MODE_SHIFT_END,
					officeGroupKeyId: this.modeRow.officeGroupKeyId
				});
			} else {
				const ms = this.hhmmToMs(this.customTimeHHmm);
				if (ms < 0 || ms >= 24 * 60 * 60 * 1000) {
					return toastService.showErrorToast("Custom time must be between 00:00 and 23:59");
				}
				settings.push({
					groupId: this.timeRow.groupId,
					keyId: this.timeRow.generalSettingsKey.id,          // 41
					keyCode: this.timeRow.generalSettingsKey.keyCode,   // "LOCATION_TRACKING_STOP_CUSTOM_TIME"
					setValue: String(ms),
					officeGroupKeyId: this.timeRow.officeGroupKeyId
				});
			}

			const payload = {
				officeId: this.selectedOffice.id,
				groupId: generalSettingsGroupIds.LOCATION_TRACKING_SETTINGS_GROUP_ID,
				settings: settings
			};

			console.log("Saving payload:", JSON.stringify(payload, null, 2));

			generalSettingsService.updateGeneralSettingsOfAnOffice(
				payload,
				() => toastService.showSuccessToast("Updated successfully"),
				(error) => {
					console.error("Save error:", error);
					toastService.showErrorToast("Update failed: " + (error.message || "Unknown error"));
				}
			);
		},

		hhmmToMs(hhmm) {
			if (!hhmm || typeof hhmm !== "string" || !/^\d{1,2}:\d{2}$/.test(hhmm)) return 0;
			const parts = hhmm.split(":");
			const h = Math.max(0, Math.min(23, parseInt(parts[0], 10) || 0));
			const m = Math.max(0, Math.min(59, parseInt(parts[1], 10) || 0));
			return (h * 60 + m) * 60 * 1000;
		},
		msToHHmm(ms) {
			if (!Number.isFinite(ms) || ms < 0) return "00:00";
			const totalMin = Math.floor(ms / 60000);
			const h = Math.floor(totalMin / 60) % 24;
			const m = totalMin % 60;
			return String(h).padStart(2, "0") + ":" + String(m).padStart(2, "0");
		}
	},
	mounted() {
		officeService.getOfficeList(r => {
			this.officeList = r;
		});
	}
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
